import { Check } from 'lucide-react';
import { TICKET_TIERS } from '../../constants/bookingFare';

function FeatureCell({ value }) {
  const lower = String(value).toLowerCase();
  const positive =
    lower.includes('included') ||
    lower.includes('free') ||
    lower === 'refundable fare' ||
    lower.includes('any seat');

  if (value === '—' || value === 'Not allowed' || value === 'Non-refundable') {
    return (
      <span className="booking-fare-table__cell booking-fare-table__cell--muted">
        {value === '—' ? '—' : value}
      </span>
    );
  }

  if (positive) {
    return (
      <span className="booking-fare-table__cell booking-fare-table__cell--yes">
        <Check size={14} aria-hidden />
        {value}
      </span>
    );
  }

  return <span className="booking-fare-table__cell">{value}</span>;
}

export default function BookingFareTable({
  selectedTierId = 'basic',
  onTierChange,
  baseFare = 0,
  travelerCount = 1,
  currency = 'USD',
}) {
  const selected = TICKET_TIERS.find((t) => t.id === selectedTierId) || TICKET_TIERS[0];
  const base = Number(baseFare) || 0;

  const tierTotal = (tier) => {
    const upgrade = tier.upgrade * travelerCount;
    const sub = base * travelerCount + upgrade;
    return sub;
  };

  const featureRows = TICKET_TIERS[0].features.map((_, index) => ({
    label: TICKET_TIERS[0].features[index].label,
    values: TICKET_TIERS.map((tier) => tier.features[index].value),
  }));

  return (
    <div className="booking-fare-select">
      <p className="booking-fare-select__lead">
        Compare fare types side by side. Your base flight fare is{' '}
        <strong>
          {currency} {(base * travelerCount).toFixed(2)}
        </strong>
        {travelerCount > 1 ? ` for ${travelerCount} travelers` : ''}. Upgrades are added per
        traveler.
      </p>

      <div className="booking-fare-table-wrap">
        <table className="booking-fare-table">
          <caption className="sr-only">Fare type comparison</caption>
          <thead>
            <tr>
              <th scope="col" className="booking-fare-table__feature-col">
                What&apos;s included
              </th>
              {TICKET_TIERS.map((tier) => (
                <th
                  key={tier.id}
                  scope="col"
                  className={`booking-fare-table__plan-col${
                    selectedTierId === tier.id ? ' booking-fare-table__plan-col--selected' : ''
                  }`}
                >
                  <span className="booking-fare-table__plan-name">{tier.name}</span>
                  <span className="booking-fare-table__plan-tag">{tier.tagline}</span>
                  <span className="booking-fare-table__plan-price">
                    {tier.upgrade === 0
                      ? 'Included'
                      : `+ ${currency} ${tier.upgrade.toFixed(2)} / traveler`}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {featureRows.map((row) => (
              <tr key={row.label}>
                <th scope="row" className="booking-fare-table__row-label">
                  {row.label}
                </th>
                {row.values.map((value, i) => (
                  <td
                    key={TICKET_TIERS[i].id + row.label}
                    className={
                      selectedTierId === TICKET_TIERS[i].id
                        ? 'booking-fare-table__plan-col--selected'
                        : ''
                    }
                  >
                    <FeatureCell value={value} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td className="booking-fare-table__footer-label">Estimated fare total</td>
              {TICKET_TIERS.map((tier) => (
                <td
                  key={tier.id}
                  className={
                    selectedTierId === tier.id
                      ? 'booking-fare-table__plan-col--selected'
                      : ''
                  }
                >
                  <span className="booking-fare-table__footer-total">
                    {currency} {tierTotal(tier).toFixed(2)}
                  </span>
                  <span className="booking-fare-table__footer-note">before taxes &amp; add-ons</span>
                </td>
              ))}
            </tr>
            <tr className="booking-fare-table__select-row">
              <td />
              {TICKET_TIERS.map((tier) => (
                <td
                  key={tier.id}
                  className={
                    selectedTierId === tier.id
                      ? 'booking-fare-table__plan-col--selected'
                      : ''
                  }
                >
                  <label className="booking-fare-table__select-btn">
                    <input
                      type="radio"
                      name="ticketTier"
                      value={tier.id}
                      checked={selectedTierId === tier.id}
                      onChange={() => onTierChange?.(tier.id)}
                    />
                    <span>
                      {selectedTierId === tier.id ? 'Selected' : 'Select'}{' '}
                      {tier.name}
                    </span>
                  </label>
                </td>
              ))}
            </tr>
          </tfoot>
        </table>
      </div>

      <div className="booking-fare-selected" role="status">
        <strong>Your selection:</strong> {selected.name} fare
        {selected.upgrade > 0 ? (
          <>
            {' '}
            — adds{' '}
            <strong>
              {currency} {(selected.upgrade * travelerCount).toFixed(2)}
            </strong>{' '}
            ({currency} {selected.upgrade.toFixed(2)} × {travelerCount} traveler
            {travelerCount > 1 ? 's' : ''})
          </>
        ) : (
          ' — no upgrade fee'
        )}
      </div>
    </div>
  );
}
